import { defineComponent as _defineComponent } from 'vue'
import {ref, computed, onMounted, Ref, watch} from "vue";
import type Language from "@/models/Language";
import NutritionLabelRepository from "@/utils/API/NutritionLabelRepository";
import {useTranslations} from "@/hooks/useTranslations";
import CollapsableCard from "@/components/bootstrap/CollapsableCard.vue";
import VCautionaryInputs from "@/components/vCautionaryInputs.vue";
import {LabelTypeMetadata} from "@/models/NutritionLabels/LabelTypeMetadata";
import {ExportLabelState} from "@/models/NutritionLabels/ExportLabelState";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";
import {vSelectOptionBase} from "@/typings/common";
import {LabelTypes} from "@/models/NutritionLabels/LabelTypes";
import {parseCountryKey} from "@/models/NutritionLabels/v2/Helpers";

type LabelTypeSelectionProps = {
  value: ExportLabelState;
  showLanguageSelection?: boolean;
  recipeId: number;
  country: string;
  initialLabelTemplate?: string;
  initialLanguage?: string;
  labelTypeFilter?: LabelTypeMetadata;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'LabelTypeSelection',
  props: {
    value: null,
    showLanguageSelection: { type: Boolean, default: true },
    recipeId: null,
    country: null,
    initialLabelTemplate: null,
    initialLanguage: null,
    labelTypeFilter: { default: null }
  },
  emits: [
  "update:value",
  "reset-model",
  "update:selectedLabelType",
  "update:primaryLanguage",
  "update:freeTextFields",
  "cautionaryState"
],
  setup(__props: any, { emit }) {

const props = __props as { value: ExportLabelState, showLanguageSelection: boolean, recipeId: number, country: string, initialLabelTemplate?: string, initialLanguage?: string, labelTypeFilter: LabelTypeMetadata };





const translations = useTranslations();
const localModel = ref(props.value);

watch(localModel, (val) => emit("update:value", val), {deep: true});

watch(
  () => props.initialLabelTemplate,
  (newValue) => {
    const savedLabel = labelTypeOptions.value.find(
      (x) => x.value === newValue
    );

    if (savedLabel) {
      selectedLabelType.value = savedLabel;
      emit("update:selectedLabelType", savedLabel);

      updateLanguagesForLabel(savedLabel.value);
    }
  }
);

const selectedLabelType: Ref<vSelectOptionBase<string>> = ref(null);
const selectedLanguage: Ref<Language> = ref(null);
const allLabelsData = ref<Record<string, any>>({});
const labelTypes = ref<string[]>([]);
const supportedLanguagesOptions = ref<Language[]>([]);

const labelTypeOptions = computed(() => {
  const rv: vSelectOptionBase<string>[] = [];

  for (const labelKey of labelTypes.value) {
    rv.push({
      name: translations.value.get(labelKey),
      value: labelKey,
    });
  }
  return rv;
});

const shouldDisplaySupplementFoodTypeOptions = computed(() => {
  return (
    selectedLabelType.value
    && selectedLabelType.value.value == LabelTypes[LabelTypes.SupplementLabel]
    && props.country === 'Canada'
  );
});

onMounted(fetchLabels);

async function fetchLabels() {
  try {
    const response = await NutritionLabelRepository.listLabelsV2(props.labelTypeFilter);
    allLabelsData.value = response.data;

    const countryKey = parseCountryKey(props.country);
    const countryLabels = allLabelsData.value[countryKey] || {};
    labelTypes.value = Object.keys(countryLabels);

    if (labelTypes.value.length > 0) {
      // Saved labelTemplate
      console.log("Saved labelTemplate:", props.initialLabelTemplate);

      const defaultLabelKey = props.initialLabelTemplate
        ? labelTypes.value.find((key) => key === props.initialLabelTemplate)
        : labelTypes.value[0];

      if (defaultLabelKey) {
        selectedLabelType.value = {
          name: translations.value.get(defaultLabelKey),
          value: defaultLabelKey,
        };

        emit("update:selectedLabelType", selectedLabelType.value);
        updateLanguagesForLabel(defaultLabelKey);
      }
    }
  }
  catch (err) {
    console.error("Failed to load NutritionLabels:", err);
  }
}

function updateFilters(selected: vSelectOptionBase<string>) {
  selectedLabelType.value = selected;
  emit("update:selectedLabelType", selected);
  updateLanguagesForLabel(selected.value);
  emit("reset-model");
}

function updateLanguagesForLabel(labelKey: string) {
  const countryKey = parseCountryKey(props.country);
  const countryLabels = allLabelsData.value[countryKey] || {};
  const languages = countryLabels[labelKey] || [];

  supportedLanguagesOptions.value = languages.map((lang: string) => ({
    name: translations.value.get(lang),
    value: lang,
  }));

  if (supportedLanguagesOptions.value.length > 0) {
    selectedLanguage.value = supportedLanguagesOptions.value.find(
        (lang) => lang.value == props.initialLanguage.toString()
    ) || supportedLanguagesOptions.value[0];

    emit("update:primaryLanguage", selectedLanguage.value.value);
  }
}

function emitLanguageChange(selectedLang: Language) {
  selectedLanguage.value = selectedLang;
  emit("update:primaryLanguage", selectedLang.value);
}

function handleCautionaryQualification(state: boolean) {
  console.log("Cautionary state changed:", state);
  emit("cautionaryState", state);
}

return { __sfc: true,props, emit, translations, localModel, selectedLabelType, selectedLanguage, allLabelsData, labelTypes, supportedLanguagesOptions, labelTypeOptions, shouldDisplaySupplementFoodTypeOptions, fetchLabels, updateFilters, updateLanguagesForLabel, emitLanguageChange, handleCautionaryQualification, CollapsableCard, VCautionaryInputs, LabelTypeMetadata, LabelLanguage }
}

})